/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { gsap } from 'gsap';
import {
    Mesh,
    MeshBasicMaterial,
    Object3D,
    PerspectiveCamera,
    PlaneGeometry,
    Scene,
    ShaderMaterial,
    Vector2,
    WebGLRenderTarget,
} from 'three';

import type Renderer from '../../globals/Renderer';

import { FloorGameWebGLVariant } from '../../types';
import Loaders from '../../utils/Loaders';
import PerformanceProfile from '../../utils/PerformanceProfile';
import SoundController from '../../utils/SoundController';

import Text from './FloorGameDown/CardDownText';
import CutEffect from './FloorGameDown/CutEffect';
import EndText from './FloorGameDown/EndText';
import TextSequence from './FloorGameDown/TextSequence';
import { soundTimeline } from './FloorGameDown/soundTimeline';
import { CardGradientBackground } from './Global';
import { effectFragmentShader, effectVertexShader } from './shaders';

const emojiSequenceTexture = {
    url: '/assets/webgl/textures/floor-down-emoji.png',
    texture: null,
};

interface CardFgDownOptions {
    type: string;
    name: string;
    floorPrice: number;
    collectionName?: string;
    isCollectionView?: boolean;
    nftDetailView?: boolean;
}

export default class CardGL extends Object3D {
    renderer: Renderer;
    scene: Scene;

    floorPrice: number;
    collectionName: string;
    isCollectionView: boolean;

    // TODO: These need to be explicitly set within the constructor.
    // Otherwise, it needs to accept `undefined`.
    background: CardGradientBackground;
    effectRenderer: WebGLRenderTarget;
    text: Text;
    textScene: Scene;
    textRendererMesh: Mesh;
    cutEffect: CutEffect;
    textSequence: TextSequence;
    endText: EndText;
    soundController: SoundController;
    effectRunning: boolean;

    time = 0;
    width = 1;
    height = 1;

    mouse = new Vector2(0, 0);

    camera = new PerspectiveCamera(30, 1, 0.01, 1000);

    constructor(
        renderer: Renderer,
        { type, name, floorPrice, collectionName, isCollectionView, nftDetailView }: CardFgDownOptions,
    ) {
        super();

        this.renderer = renderer;
        this.scene = new Scene();

        this.type = type;
        this.name = name;
        this.floorPrice = floorPrice;
        this.collectionName = collectionName ?? '';
        this.isCollectionView = Boolean(isCollectionView);

        this.camera.position.z = 5;
        this.scene.add(this);

        this.initGradientBackground();
        this.initTextAnimation();
        this.initCutEffect(Boolean(nftDetailView));
        this.initTextSequence();
        this.initEmojiSequence();
        this.initEndText();
        this.initSound();

        if (PerformanceProfile.devMode) this.addGUI();
    }

    initGradientBackground() {
        this.background = new CardGradientBackground({
            type: FloorGameWebGLVariant.TierDown,
        });

        this.add(this.background);
    }

    initTextAnimation() {
        this.effectRenderer = new WebGLRenderTarget(1, 1);

        this.textScene = new Scene();
        this.text = new Text({
            card: this,
            floorPrice: this.floorPrice,
        });
        this.textScene.add(this.text);

        const geometry = new PlaneGeometry(1, 1);
        const material = new ShaderMaterial({
            fragmentShader: effectFragmentShader,
            vertexShader: effectVertexShader,
            uniforms: {
                uTexture: { value: this.effectRenderer.texture },
                uTransition: { value: 0.0 },
                uCutTransition: { value: 0.0 },
                uAlpha: { value: 0.0 },
            },
            transparent: true,
        });

        this.textRendererMesh = new Mesh(geometry, material);
        this.textRendererMesh.position.z = 1.5;
        this.textRendererMesh.position.y += 0.1;

        this.add(this.textRendererMesh);
    }

    initCutEffect(isDetailView) {
        this.cutEffect = new CutEffect({ isDetailView });
        this.add(this.cutEffect);
        this.cutEffect.position.z = 2.0;
        this.cutEffect.position.y += 0.1 * (2.0 / 1.5);
    }

    initTextSequence() {
        this.textSequence = new TextSequence();
        this.add(this.textSequence);
        this.textSequence.position.x = -0.3;
        this.textSequence.position.y = 0.32;
        this.textSequence.position.z = 3;
    }

    initEmojiSequence() {
        const geometry = new PlaneGeometry(1, 1);
        const material = new MeshBasicMaterial({
            transparent: true,
        });

        if (emojiSequenceTexture.texture) {
            material.map = emojiSequenceTexture.texture;
            material.map.needsUpdate = true;
        } else {
            Loaders.textureLoader.load(emojiSequenceTexture.url, (texture) => {
                emojiSequenceTexture.texture = texture;
                material.map = texture;
                material.map.needsUpdate = true;
                material.needsUpdate = true;
            });
        }

        const meshA = new Mesh(geometry, material);
        this.add(meshA);

        const meshB = new Mesh(geometry, material);
        this.add(meshB);

        meshA.visible = false;
        meshB.visible = false;

        meshA.position.x = 0.3;
        meshA.scale.set(0.6, 0.6, 1.0);
        meshA.position.y = -0.15;

        meshB.scale.set(1.2, 1.2, 1.0);
        meshB.__pulse = 0;

        this.emojis = [meshA, meshB];
    }

    initEndText() {
        this.endText = new EndText({
            floorPrice: this.floorPrice,
            collectionName: this.collectionName,
        });
        this.add(this.endText);
        this.endText.position.y = -0.2;
    }

    initSound() {
        this.soundController = new SoundController(soundTimeline, '/assets/webgl/audio/floor-game-down/');
    }

    /*
    setDomElement() {
        if (this.domElement) {
            this.domElement.removeEventListener('mousemove', this.onMouseMove);
            this.domElement.removeEventListener('mouseleave', this.onMouseLeave);
        }

        this.domElement = domElement;
        this.domElement.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.domElement.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }

    onMouseMove(event) {
        if (!this.layer.visible) return;

        this.mouseEase = 0.03;
        this.mouseTarget.x = map(
            event.clientX,
            this.layer.position.x,
            this.layer.position.x + this.layer.width,
            -1,
            1,
            true
        );
        this.mouseTarget.y = map(
            event.clientY,
            -this.layer.position.y,
            -this.layer.position.y + this.layer.height,
            -1,
            1,
            true
        );
    }
    */

    /*
    onMouseLeave() {
        if (!this.layer.visible) return;

        this.mouseEase = 0.01;
        this.mouseTarget.x = 0;
        this.mouseTarget.y = 0;
    }
    */

    setSize(width: number, height: number) {
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        if (this.isCollectionView) {
            const ratio = Math.max(800 / height, 1.0);
            this.textRendererMesh.scale.set(10 * ratio, 10 * (height / width) * ratio, 1);
        } else {
            if (width * 0.5 < 1100) this.textRendererMesh.scale.set(1.3, 1.3 * (height / width), 1);
            else this.textRendererMesh.scale.set(3, 3 * (height / width), 1);
        }

        this.effectRenderer.setSize(width, height);

        if (this.text) {
            this.text.setPosition(width, height);
        }
    }

    animateIn() {
        this.effectRunning = true;
        this.emojis[0].visible = false;
        this.emojis[1].visible = false;

        gsap.fromTo(
            this.textRendererMesh.material.uniforms.uAlpha,
            {
                value: 0,
            },
            {
                value: 1,
                ease: 'power2.out',
                duration: 0.4,
                delay: 0.1,
            },
        );
        gsap.fromTo(
            this.textRendererMesh.material.uniforms.uTransition,
            {
                value: 0,
            },
            {
                value: 1,
                ease: 'expo.out',
                duration: 2.4,
                delay: 0.15,
            },
        );

        this.text.animateIn();
        this.background.animateIn(1.1);
        this.cutEffect.animateIn(1.52);

        gsap.fromTo(
            this.textRendererMesh.material.uniforms.uCutTransition,
            { value: 0 },
            {
                value: 1.2,
                ease: 'expo.out',
                duration: 2.0,
                delay: 2.0,
            },
        );

        this.textSequence.animateIn(2.2);

        gsap.fromTo(
            this.textRendererMesh.material.uniforms.uAlpha,
            { value: 1 },
            {
                value: 0,
                ease: 'power3.Out',
                duration: 0.5,
                delay: 2.2,
            },
        );

        gsap.fromTo(
            this.emojis[0].rotation,
            {
                z: Math.PI * 0.5,
            },
            {
                z: 0,
                duration: 0.5,
                delay: 2.4,
                ease: 'power1.out',
            },
        );

        gsap.fromTo(
            this.emojis[0].position,
            {
                y: 2.0,
            },
            {
                y: -0.15,
                duration: 1.4,
                delay: 2.2,
                ease: 'expo.out',
                onStart: () => {
                    this.emojis[0].visible = true;
                },
            },
        );

        this.textSequence.animateOut(5);

        gsap.to(this.emojis[0].position, {
            y: 2.3,
            duration: 1.2,
            delay: 5,
            ease: 'power1.inOut',
            onComplete: () => {
                this.emojis[0].visible = false;
            },
        });

        gsap.fromTo(
            this.emojis[1].rotation,
            {
                z: Math.PI * 0.1,
            },
            {
                z: 0,
                duration: 0.6,
                delay: 5.3,
                ease: 'power1.out',
            },
        );

        gsap.fromTo(
            this.emojis[1].position,
            {
                y: -2.3,
            },
            {
                y: 0.3,
                duration: 1.4,
                delay: 5.3,
                ease: 'expo.out',
                onStart: () => {
                    this.emojis[1].visible = true;
                },
            },
        );

        this.endText.animateIn(5.5);

        gsap.fromTo(
            this.emojis[1],
            {
                __pulse: 0,
            },
            {
                __pulse: Math.PI * 5.0,
                duration: 4,
                delay: 5.0,
                ease: 'power2.inOut',
                onUpdate: () => {
                    const s = Math.sin(this.emojis[1].__pulse) * 0.1;
                    this.emojis[1].scale.set(1.2 + s, 1.2 + s, 1);
                },
            },
        );

        this.soundController.playTimeline();
    }

    // animateOut() {
    // console.log('out');
    // }

    update(elapsed: number, delta: number) {
        this.time += 0.01;

        // this.textRendererMesh.material.uniforms.uCutTransition.value = (this.time * 0.5) % 1.5;

        if (this.background) {
            this.background.update(elapsed, delta);
        }

        if (this.effectRunning && this.text.ready && this.text.visible) {
            // only need one frame? verify if it's loaded
            this.layer.manager.renderer.setRenderTarget(this.effectRenderer);
            this.layer.manager.renderer.render(this.textScene, this.camera);
            this.layer.manager.renderer.setRenderTarget(null);
            this.effectRunning = false;
        }

        // Parrallax Effect
        /*
        if (this.canInteract) {
            this.mouse.lerp(this.mouseTarget, this.mouseEase);
        }

        this.camera.position.x = this.mouse.x * 1.0;
        this.camera.position.y = this.mouse.y * -2.0;
        this.camera.lookAt(this.lookAtTarget);
        */
    }

    delete() {
        if (this.domElement) {
            this.domElement.removeEventListener('mousemove', this.onMouseMove);
            this.domElement.removeEventListener('mouseleave', this.onMouseLeave);
        }

        this.emojis.forEach((mesh) => {
            mesh.material && mesh.material.dispose();
            mesh.geometry && mesh.geometry.dispose();
        });

        if (this.effectRenderer) this.effectRenderer.dispose();
        if (this.text) this.text.delete();
        if (this.textSequence) this.textSequence.delete();
        if (this.endText) this.endText.delete();

        this.soundController.killTimeline();

        if (PerformanceProfile.devMode) {
            this.GUI.dispose();
        }
    }

    addGUI() {
        this.GUI = PerformanceProfile.GUI.floorGameDown.addFolder({
            title: `${this.name} Card:`,
            expanded: false,
        });

        this.GUI.addButton({
            title: 'Animate In',
        }).on('click', () => {
            this.animateIn(this.type);
        });

        /*
        this.GUI.addButton({
            title: 'Animation Out'
        }).on('click', this.animateOut.bind(this));
        */
    }
}
